import {
  USAGE_BUCKETS,
  USAGE_BUCKET_TEXT_COLOR,
  UNKNOWN_BUCKET,
  REVIEW_HOURS,
  formatUsage
} from "./usageStatistics";

export function distributionChartData(groups) {
  const buckets = groups.some((group) => group.unknown)
    ? [...USAGE_BUCKETS, UNKNOWN_BUCKET]
    : USAGE_BUCKETS;
  return {
    labels: groups.map(
      (group) => `${group.label} (${group.total.toLocaleString()}대)`
    ),
    datasets: buckets.map((bucket, index) => ({
      label: bucket.label,
      backgroundColor: bucket.color,
      hoverBackgroundColor: bucket.color,
      borderWidth: 0,
      maxBarThickness: 30,
      data: groups.map(
        (group) =>
          ((index === USAGE_BUCKETS.length
            ? group.unknown
            : group.counts[index]) /
            group.total) *
          100
      )
    }))
  };
}

export function deviceChartData(devices) {
  return {
    labels: devices.map((device) => device.deviceLabel),
    datasets: [
      {
        label: "총 사용시간",
        backgroundColor: "#6095af",
        maxBarThickness: 28,
        data: devices.map((device) =>
          device.seconds === null ? null : device.seconds / 3600
        )
      }
    ]
  };
}

export function statisticsChartOptions({
  deviceMode,
  axisMax,
  groups,
  devices,
  select
}) {
  return {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    layout: {
      padding: { top: deviceMode ? 30 : 8, right: deviceMode ? 160 : 12 }
    },
    onClick: (_event, elements) => {
      if (!deviceMode && elements.length) select(groups[elements[0].index]);
    },
    onHover: (event, elements) => {
      if (event.native?.target)
        event.native.target.style.cursor =
          !deviceMode && elements.length ? "pointer" : "default";
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) =>
            deviceMode
              ? devices[items[0].dataIndex].deviceLabel
              : groups[items[0].dataIndex].label,
          label: (context) => {
            if (deviceMode)
              return formatUsage(devices[context.dataIndex].seconds);
            const group = groups[context.dataIndex];
            const count =
              context.datasetIndex === USAGE_BUCKETS.length
                ? group.unknown
                : group.counts[context.datasetIndex];
            return `${
              context.dataset.label
            }: ${count.toLocaleString()}대 (${Number(context.raw).toFixed(
              1
            )}%)`;
          }
        }
      }
    },
    scales: {
      x: {
        stacked: !deviceMode,
        min: 0,
        max: deviceMode ? axisMax : 100,
        ticks: {
          callback: (value) =>
            deviceMode ? `${Number(value).toLocaleString()}시간` : `${value}%`
        },
        grid: { color: "#eeeeee" }
      },
      y: {
        stacked: !deviceMode,
        grid: { display: false },
        afterFit: (axis) => {
          axis.width = 200;
        },
        ticks: {
          autoSkip: false,
          callback: function (value) {
            const label = this.getLabelForValue(value);
            return label.length > 24 ? `${label.slice(0, 23)}…` : label;
          }
        }
      }
    }
  };
}

// Scoped per chart: no global registration or external annotation dependency.
export function statisticsLabelsPlugin({ deviceMode, devices }) {
  return {
    id: "usageStatisticsLabels",
    afterDatasetsDraw(chart) {
      const { ctx, chartArea, scales } = chart;
      ctx.save();
      ctx.font = "12px sans-serif";
      if (deviceMode) {
        const x = scales.x.getPixelForValue(REVIEW_HOURS);
        ctx.strokeStyle = "#666666";
        ctx.setLineDash([5, 4]);
        ctx.beginPath();
        ctx.moveTo(x, chartArea.top);
        ctx.lineTo(x, chartArea.bottom);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = "#444444";
        ctx.textAlign = "right";
        ctx.fillText(
          "교체 검토 기준 50,000시간",
          Math.max(chartArea.left + 175, x),
          chartArea.top - 12
        );
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        devices.forEach((device, index) =>
          ctx.fillText(
            formatUsage(device.seconds),
            chartArea.right + 10,
            scales.y.getPixelForValue(index)
          )
        );
      } else {
        chart.data.datasets.forEach((dataset, datasetIndex) => {
          chart.getDatasetMeta(datasetIndex).data.forEach((bar, index) => {
            const value = dataset.data[index];
            const label = `${value.toFixed(1)}%`;
            if (
              value > 0 &&
              Math.abs(bar.x - bar.base) > ctx.measureText(label).width + 10
            ) {
              ctx.fillStyle =
                USAGE_BUCKETS[datasetIndex]?.textColor ||
                USAGE_BUCKET_TEXT_COLOR;
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";
              ctx.fillText(label, (bar.x + bar.base) / 2, bar.y);
            }
          });
        });
      }
      ctx.restore();
    }
  };
}
